import type { Profession } from "@brookbush/types";

import { Listbox } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsUpDown } from "lucide-react";

import { useProfessions } from "~/hooks";

interface SelectAProfessionFieldProps {
	value: Profession | null;
	onChange: (profession: Profession | null) => void;
	filterFn: (profession: Profession) => boolean;
	label: string;
	id: string;
	placeholder: string;
	disabled?: boolean;
	isModal?: boolean;
}

const SelectAProfessionField = ({
	label,
	value,
	onChange,
	filterFn,
	id,
	placeholder,
	disabled,
	isModal,
}: SelectAProfessionFieldProps) => {
	const { professions } = useProfessions();

	return (
		<div className="relative">
			<Listbox value={value} onChange={onChange} disabled={disabled}>
				{({ open }) => (
					<>
						<div
							className={clsx(
								"flex flex-row items-center justify-between gap-2 sm:gap-4",
								{
									"opacity-50": disabled,
								},
							)}
						>
							<label htmlFor={id} className="font-medium">
								{label}
							</label>

							<Listbox.Button
								id={id}
								className="flex w-64 items-center justify-center gap-1 overflow-ellipsis whitespace-nowrap rounded-lg border px-4 py-1.5 transition-all duration-200 hover:opacity-50 dark:hover:opacity-75"
							>
								{value?.title || placeholder}
								<ChevronsUpDown size={18} className="shrink-0" />
							</Listbox.Button>
						</div>

						<AnimatePresence mode="wait">
							{open && (
								<Listbox.Options
									static
									as={motion.div}
									initial={{ opacity: 0, y: -20 }}
									animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
									exit={{
										opacity: 0,
										y: 20,
									}}
									className={clsx(
										"absolute right-0 z-50 flex max-h-64 w-64 flex-col gap-1 overflow-scroll rounded-xl border bg-white p-1.5 shadow-black/5 shadow-xl sm:top-12 sm:bottom-auto sm:left-[8rem] sm:w-64 sm:shadow-black/10 dark:bg-slate-800",
										isModal ? "bottom-12" : "top-12",
									)}
								>
									{id === "secondary-profession" && value && (
										<button
											type="button"
											onClick={() => {
												onChange(null);
											}}
											className="z-20 cursor-pointer whitespace-nowrap rounded-lg bg-red-50 px-3.5 py-1.5 font-medium text-red-700 hover:bg-red-100 dark:bg-red-300/10 dark:text-red-300 dark:hover:bg-red-300/20"
										>
											Clear Selection
										</button>
									)}

									{professions!.filter(filterFn).map((prof) => (
										<Listbox.Option
											key={prof._id}
											value={prof}
											className={clsx(
												"z-20 cursor-pointer list-none whitespace-nowrap rounded-lg px-3.5 py-1.5",
												{
													"bg-slate-50 font-medium text-black dark:bg-slate-700 dark:text-white":
														prof.slug === value?.slug,
													"hover:bg-slate-50 hover:text-black dark:text-white dark:hover:bg-slate-700/90 dark:hover:text-white":
														prof.slug !== value?.slug,
												},
											)}
										>
											{prof.title}
										</Listbox.Option>
									))}
								</Listbox.Options>
							)}
						</AnimatePresence>
					</>
				)}
			</Listbox>
		</div>
	);
};

export default SelectAProfessionField;
